import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import Layout from '../Layout';

const CategoriasPanel = () => {
  const [categorias, setCategorias] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    categoria: '',
  });

  useEffect(() => {
    fetchCategorias();
  }, []);

  const fetchCategorias = async () => {
    try {
      const response = await api.get('/categorias');
      
      console.log('Resposta da API de categorias:', response.data);
      
      // Verificar se a resposta é um array (formato direto do backend)
      if (Array.isArray(response.data)) {
        setCategorias(response.data);
      } 
      // Verificar se a resposta tem a propriedade categorias
      else if (response.data && response.data.categorias) {
        setCategorias(response.data.categorias);
      } 
      // Se não, verificar se há alguma outra propriedade que contenha os dados
      else if (response.data && typeof response.data === 'object') {
        // Tentar encontrar um array na resposta
        const possibleArray = Object.values(response.data).find(value => Array.isArray(value));
        if (possibleArray) {
          setCategorias(possibleArray);
        } else {
          console.warn('Resposta não contém lista de categorias:', response.data);
          setCategorias([]);
        }
      } else {
        console.warn('Formato de resposta não reconhecido:', response.data);
        setCategorias([]);
      }
    } catch (error) {
      console.error('Erro ao buscar categorias:', error.response || error);
      setError('Erro ao carregar categorias. Por favor, tente novamente.');
    }
  };

  const handleSubmit = async () => {
    try {
      // Transformar os dados para o formato esperado pelo backend
      const dataToSend = {
        Categoria: formData.categoria
      };
      
      if (editingCategoria) {
        await api.put(`/categorias/${editingCategoria.id}`, dataToSend);
        setSuccess('Categoria atualizada com sucesso!');
      } else {
        await api.post('/categorias', dataToSend);
        setSuccess('Categoria adicionada com sucesso!');
      }
      
      setOpenDialog(false);
      setEditingCategoria(null);
      setFormData({ categoria: '' });
      fetchCategorias();
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      setError('Erro ao salvar. Por favor, verifique os dados e tente novamente.');
    }
  };

  const handleEdit = (categoria) => {
    setEditingCategoria(categoria);
    setFormData({ categoria: categoria.Categoria || '' });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      try {
        await api.delete(`/categorias/${id}`);
        setSuccess('Categoria excluída com sucesso!');
        fetchCategorias();
        
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } catch (error) {
        console.error('Erro ao excluir categoria:', error);
        setError('Erro ao excluir. Por favor, tente novamente.');
      }
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Gerenciamento de Categorias
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cadastre e gerencie as categorias do sistema
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setEditingCategoria(null);
              setFormData({ categoria: '' });
              setOpenDialog(true);
            }}
            startIcon={<AddIcon />}
          >
            Nova Categoria
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categorias.length > 0 ? (
                categorias.map((categoria) => (
                  <TableRow key={categoria.id}>
                    <TableCell>{categoria.id}</TableCell>
                    <TableCell>{categoria.Categoria}</TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleEdit(categoria)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDelete(categoria.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    Nenhum registro encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingCategoria ? 'Editar Categoria' : 'Nova Categoria'}
          </DialogTitle>
          <DialogContent>
            <TextField
              label="Nome da Categoria"
              name="categoria"
              value={formData.categoria}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default CategoriasPanel; 